import { useState } from "react";
import {
  useStores,
  useUsers,
  useCreateStore,
  useCreateUser,
} from "../hooks/useApi";

interface Store {
  id: string;
  code: string;
  name: string;
}

interface User {
  id: string;
  email: string;
  fullName: string | null;
  role: "ADMIN" | "STORE";
  createdAt: string;
  store: Store | null;
}

export function AdminUsers() {
  return (
    <div style={{ padding: "1rem", display: "grid", gap: "2rem" }}>
      <UsersSection />
      <StoresSection />
    </div>
  );
}

function UsersSection() {
  const { data: users, isLoading } = useUsers();
  const { data: stores } = useStores();
  const createUser = useCreateUser();

  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<"ADMIN" | "STORE">("STORE");
  const [storeId, setStoreId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (role === "STORE" && !storeId) {
      setError("Pick a store for a STORE user");
      return;
    }

    try {
      await createUser.mutateAsync({
        email,
        password,
        fullName: fullName || undefined,
        role,
        storeId: role === "STORE" ? storeId : undefined,
      });
      setSuccess(`User ${email} created`);
      setEmail("");
      setPassword("");
      setFullName("");
      setStoreId("");
      setShowForm(false);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to create user");
    }
  };

  return (
    <section>
      <div style={sectionHeader}>
        <h1 style={{ margin: 0 }}>Users</h1>
        <button onClick={() => setShowForm(!showForm)} style={primaryButton}>
          {showForm ? "Close" : "+ New user"}
        </button>
      </div>

      {error && <Banner tone="error">{error}</Banner>}
      {success && <Banner tone="success">{success}</Banner>}

      {showForm && (
        <form onSubmit={submit} style={formCard}>
          <div style={formGrid}>
            <Field label="Email">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={inputStyle}
              />
            </Field>
            <Field label="Password">
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                placeholder="min 8 characters"
                style={inputStyle}
              />
            </Field>
            <Field label="Full name (optional)">
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="Role">
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as "ADMIN" | "STORE")}
                style={inputStyle}
              >
                <option value="STORE">STORE</option>
                <option value="ADMIN">ADMIN</option>
              </select>
            </Field>
            {role === "STORE" && (
              <Field label="Store">
                <select
                  value={storeId}
                  onChange={(e) => setStoreId(e.target.value)}
                  style={inputStyle}
                >
                  <option value="">Select…</option>
                  {((stores as Store[]) || []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.code} — {s.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </div>
          <button type="submit" disabled={createUser.isPending} style={primaryButton}>
            {createUser.isPending ? "Creating…" : "Create user"}
          </button>
        </form>
      )}

      {isLoading ? (
        <p>Loading…</p>
      ) : (
        <div style={tableWrap}>
          <table style={tableStyle}>
            <thead style={{ backgroundColor: "#f8f9fa" }}>
              <tr>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Role</th>
                <th style={thStyle}>Store</th>
              </tr>
            </thead>
            <tbody>
              {((users as User[]) || []).map((u) => (
                <tr key={u.id} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{u.email}</td>
                  <td style={tdStyle}>{u.fullName ?? "–"}</td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        fontSize: "0.75rem",
                        padding: "0.15rem 0.5rem",
                        borderRadius: "10px",
                        backgroundColor: u.role === "ADMIN" ? "#fde8ef" : "#e7f1ff",
                        color: u.role === "ADMIN" ? "#a01048" : "#0056b3",
                      }}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {u.store ? `${u.store.code} — ${u.store.name}` : "–"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function StoresSection() {
  const { data: stores, isLoading } = useStores();
  const createStore = useCreateStore();

  const [showForm, setShowForm] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await createStore.mutateAsync({ code, name });
      setCode("");
      setName("");
      setShowForm(false);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to create store");
    }
  };

  return (
    <section>
      <div style={sectionHeader}>
        <h1 style={{ margin: 0 }}>Stores</h1>
        <button onClick={() => setShowForm(!showForm)} style={primaryButton}>
          {showForm ? "Close" : "+ New store"}
        </button>
      </div>

      {error && <Banner tone="error">{error}</Banner>}

      {showForm && (
        <form onSubmit={submit} style={formCard}>
          <div style={formGrid}>
            <Field label="Code">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                placeholder="e.g. S11"
                style={inputStyle}
              />
            </Field>
            <Field label="Name">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                style={inputStyle}
              />
            </Field>
          </div>
          <button type="submit" disabled={createStore.isPending} style={primaryButton}>
            {createStore.isPending ? "Creating…" : "Create store"}
          </button>
        </form>
      )}

      {isLoading ? (
        <p>Loading…</p>
      ) : (
        <div style={tableWrap}>
          <table style={tableStyle}>
            <thead style={{ backgroundColor: "#f8f9fa" }}>
              <tr>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Name</th>
              </tr>
            </thead>
            <tbody>
              {((stores as Store[]) || []).map((s) => (
                <tr key={s.id} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{s.code}</td>
                  <td style={tdStyle}>{s.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
      <span style={{ fontSize: "0.75rem", color: "#666" }}>{label}</span>
      {children}
    </label>
  );
}

function Banner({
  tone,
  children,
}: {
  tone: "error" | "success";
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        backgroundColor: tone === "error" ? "#fee" : "#efe",
        color: tone === "error" ? "#c33" : "#282",
        padding: "0.75rem",
        borderRadius: "4px",
        marginBottom: "1rem",
      }}
    >
      {children}
    </div>
  );
}

const sectionHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap" as const,
  gap: "0.75rem",
  marginBottom: "1rem",
};

const formCard = {
  border: "1px solid #007bff33",
  backgroundColor: "#f5f9ff",
  borderRadius: "4px",
  padding: "1rem",
  marginBottom: "1rem",
};

const formGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "0.75rem",
  marginBottom: "0.75rem",
};

const inputStyle = {
  padding: "0.5rem",
  border: "1px solid #ccc",
  borderRadius: "4px",
};

const primaryButton = {
  padding: "0.5rem 1rem",
  backgroundColor: "#007bff",
  color: "white",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
};

const tableWrap = {
  overflowX: "auto" as const,
  border: "1px solid #ddd",
  borderRadius: "4px",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse" as const,
};

const thStyle = {
  padding: "0.6rem 0.75rem",
  textAlign: "left" as const,
  fontWeight: "bold" as const,
  borderBottom: "2px solid #ddd",
};

const tdStyle = {
  padding: "0.6rem 0.75rem",
  borderBottom: "1px solid #eee",
};
